import { useEffect, useRef, useState } from 'react'
import HeaderSection from '../components/Shared/HeaderSection'
import Modal from '../components/Shared/Modal'
import SegmentedControl from '../components/Shared/SegmentedControl'
import IncidentDetail from '../components/Incident/IncidentDetail'
import IncidentForm from '../components/Incident/IncidentForm'
import IncidentTable from '../components/Incident/IncidentTable'
import AuthAccessValidator from '../components/Auth/AuthAccessValidator'
import useIncidents from '../hooks/useIncidents'
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
} from '../services/incidentService'
import { INCIDENT_STATUS_META } from '../utils/incidentTable'

/** Espera tras la última tecla antes de buscar (la búsqueda va al servidor). */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Página de incidentes de seguridad del SGSI (schema `InventoryIT`).
 * Misma línea que Gestión de Tickets: cabecera compacta, una barra con estado
 * (con conteos), severidad y búsqueda, y una tabla que ocupa el alto restante.
 *
 * Se monta sólo con acceso concedido (ver {@link Incidents}) para no lanzar
 * las consultas al SGSI a usuarios sin permiso.
 *
 * @component
 * @returns {JSX.Element}
 */
const IncidentsContent = () => {
  const {
    incidents,
    assets,
    loading,
    saving,
    filters,
    setFilters,
    stats,
    author,
    refresh,
    create,
    update,
    changeStatus,
    remove,
  } = useIncidents()

  /**
   * Estado del modal: `mode` define qué se renderiza y `incident` el registro
   * involucrado (null al registrar uno nuevo).
   * @type {[{mode: 'create'|'edit'|'detail'|null, incident: Object|null}, Function]}
   */
  const [modal, setModal] = useState({ mode: null, incident: null })
  const [searchInput, setSearchInput] = useState(filters.search)
  const searchTimer = useRef(null)

  useEffect(() => () => clearTimeout(searchTimer.current), [])

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  /** Búsqueda al escribir, con espera para no consultar en cada tecla. */
  const handleSearchChange = (value) => {
    setSearchInput(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(
      () => setFilter('search', value.trim()),
      SEARCH_DEBOUNCE_MS
    )
  }

  const clearAll = () => {
    clearTimeout(searchTimer.current)
    setSearchInput('')
    setFilters({ status: '', severity: '', search: '' })
  }

  const hasFilters = !!(filters.status || filters.severity || filters.search)

  const statusOptions = [
    { value: '', label: 'Todos', count: stats.total },
    ...INCIDENT_STATUSES.map((status) => ({
      value: status,
      label: status,
      count: stats[INCIDENT_STATUS_META[status]?.countKey] ?? 0,
    })),
  ]

  const closeModal = () => setModal({ mode: null, incident: null })
  const openCreate = () => setModal({ mode: 'create', incident: null })

  const handleDelete = async (incident) => {
    const confirmed = window.confirm(
      `¿Eliminar el incidente ${incident.Code}? Esta acción no se puede deshacer.`
    )
    if (confirmed) await remove(incident)
  }

  const modalTitle =
    modal.mode === 'create'
      ? 'Registrar incidente de seguridad'
      : modal.mode === 'edit'
        ? `Editar ${modal.incident?.Code}`
        : `${modal.incident?.Code} · ${modal.incident?.Severity}`

  return (
    <div className="page-fill">
      <Modal
        isOpen={!!modal.mode}
        onClose={closeModal}
        title={modalTitle}
        isLarge
      >
        {modal.mode === 'detail' && modal.incident ? (
          <IncidentDetail incident={modal.incident} />
        ) : modal.mode ? (
          <IncidentForm
            // Remonta el formulario al cambiar de registro/modo para que
            // recalcule sus valores iniciales.
            key={`${modal.mode}-${modal.incident?.Id ?? 'new'}`}
            incident={modal.mode === 'edit' ? modal.incident : null}
            assets={assets}
            author={author}
            saving={saving}
            onSubmit={(payload, assetIds) =>
              modal.mode === 'edit'
                ? update(modal.incident.Id, payload, assetIds)
                : create(payload, assetIds)
            }
            onCancel={closeModal}
          />
        ) : null}
      </Modal>

      <HeaderSection
        compact
        title="Gestión de Incidentes"
        subtitle="Bitácora del SGSI (ISO 27001): triage, seguimiento y cierre"
        icon="fa-solid fa-shield-halved"
        buttonText="Registrar incidente"
        buttonIcon="fa-solid fa-plus"
        onButtonClick={openCreate}
      />

      {/* BARRA ÚNICA: estado (con conteos) + severidad + búsqueda */}
      <div className="inc-toolbar mb-3">
        <div className="inc-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="search"
            placeholder="Buscar por código o descripción…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Buscar incidentes"
          />
        </div>

        <select
          className="inc-select"
          value={filters.severity}
          onChange={(e) => setFilter('severity', e.target.value)}
          aria-label="Severidad"
        >
          <option value="">Toda severidad</option>
          {INCIDENT_SEVERITIES.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>

        {hasFilters && (
          <button
            type="button"
            className="inc-btn"
            onClick={clearAll}
            title="Quitar búsqueda y filtros"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}

        <div className="inc-toolbar-end">
          <SegmentedControl
            ariaLabel="Estado"
            options={statusOptions}
            value={filters.status}
            onChange={(value) => setFilter('status', value)}
          />
          <button
            type="button"
            className="inc-btn"
            onClick={refresh}
            title="Recargar"
            aria-label="Recargar"
          >
            <i
              className={`fa-solid fa-rotate-right ${loading ? 'fa-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      <IncidentTable
        incidents={incidents}
        loading={loading}
        total={stats.total}
        hasFilters={hasFilters}
        onOpen={(incident) => setModal({ mode: 'detail', incident })}
        onEdit={(incident) => setModal({ mode: 'edit', incident })}
        onChangeStatus={changeStatus}
        onDelete={handleDelete}
        onCreate={openCreate}
      />

      <style>{`
        .inc-toolbar {
          flex-shrink: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
          padding: 8px; border: 1px solid var(--gm-border); border-radius: 12px;
          background: var(--gm-surface);
        }
        .inc-search {
          flex: 1 1 240px; max-width: 380px; display: flex; align-items: center; gap: 8px;
          height: 34px; padding: 0 12px; border-radius: 8px;
          border: 1px solid var(--gm-border); background: var(--gm-surface-2);
          color: var(--gm-text-faint); transition: border-color 0.15s ease;
        }
        .inc-search:focus-within { border-color: var(--gm-primary); }
        .inc-search i { font-size: 0.8rem; }
        .inc-search input {
          flex: 1; min-width: 0; border: none; outline: none; background: transparent;
          font-size: 0.85rem; color: var(--gm-text);
        }
        .inc-select {
          height: 34px; padding: 0 28px 0 10px; border-radius: 8px;
          border: 1px solid var(--gm-border); background-color: var(--gm-surface);
          color: var(--gm-text); font-size: 0.82rem;
        }
        .inc-select:focus { outline: none; border-color: var(--gm-primary); }
        .inc-btn {
          height: 34px; width: 34px; display: inline-flex; align-items: center; justify-content: center;
          border-radius: 8px; border: 1px solid var(--gm-border); background: transparent;
          color: var(--gm-text-muted); font-size: 0.8rem;
        }
        .inc-btn:hover { color: var(--gm-text); }
        .inc-toolbar-end { margin-left: auto; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
      `}</style>
    </div>
  )
}

/**
 * Página de incidentes con la validación de permisos aplicada.
 * Requiere el mismo permiso que el resto del área de soporte.
 *
 * @component
 * @returns {JSX.Element}
 */
const Incidents = () => (
  <AuthAccessValidator AccessNames={['ticket_support']} showError={true}>
    <IncidentsContent />
  </AuthAccessValidator>
)

export default Incidents
